import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, Input, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import type { FormControl, FormGroup } from '@angular/forms';
import { NonNullableFormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { map, takeUntil } from 'rxjs/operators';
import { BsModalRef } from 'ngx-bootstrap/modal';

import type { SitePageEntity } from '@common/types/site-page.type';
import { ESavePageErrorCodes } from '@common/enums/error-cores.enum';

import { SiteFacade } from '@web/store/website/site/site.facade';
import { PagesFacade } from '@web/store/website/pages/pages.facade';
import { DomainsFacade } from '@web/store/website/domains/domains.facade';

import SiteActions from '@web/store/website/site/site.actions';
import { PageNameValidatorDirective } from '@web/core/validators/page-name.validator';
import { AsyncPipe } from '@angular/common';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { PageUrlValidatorDirective } from '@web/core/validators/page-url.validator';
import { TranslateModule } from '@ngx-translate/core';

type NewPageForm = {
    name: FormControl<SitePageEntity['name']>;
    pageUrl: FormControl<SitePageEntity['url']>;
};

type NewPageFormGroup = FormGroup<NewPageForm>;

@Component({
    selector: 'web-create-new-page-modal',
    templateUrl: './create-new-page-modal.component.html',
    styleUrls: ['./create-new-page-modal.component.less'],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        ReactiveFormsModule,
        PageNameValidatorDirective,
        TooltipModule,
        PageUrlValidatorDirective,
        AsyncPipe,
        TranslateModule,
    ],
})
export class CreateNewPageModalComponent implements OnInit, OnDestroy {
    private readonly bsModalRef = inject(BsModalRef);
    private readonly fb = inject(NonNullableFormBuilder);
    private readonly actions$ = inject(Actions);
    private readonly changeDetector = inject(ChangeDetectorRef);
    private readonly siteFacade = inject(SiteFacade);
    private readonly pagesFacade = inject(PagesFacade);
    private readonly domainsFacade = inject(DomainsFacade);

    @Input() public initialName!: string;
    @Input() public initialPageUrl!: string;

    @Input() public siteId: string;
    @Input() public pageId: string;
    @Input() public exit: boolean;
    @Input() public publish: boolean;
    @Input() public navigateTo: { pageId?: string; siteId?: string; isNew?: boolean };

    public form: NewPageFormGroup;

    public readonly NAME_MIN_LENGTH = 1;

    private readonly unsubscribe$ = new Subject<void>();

    public ngOnInit(): void {
        this.initForm();

        this.actions$.pipe(ofType(SiteActions.saveSiteFailure), takeUntil(this.unsubscribe$)).subscribe(({ error }) => {
            this.form.enable({ emitEvent: false });
            this.changeDetector.detectChanges();
        });

        this.actions$.pipe(ofType(SiteActions.saveSiteSuccess), takeUntil(this.unsubscribe$)).subscribe(() => {
            this.bsModalRef.hide();
        });

        this.setFormData();
    }

    private setFormData(): void {
        this.form.patchValue({
            name: this.initialName,
            pageUrl: this.initialPageUrl,
        });
    }

    private initForm(): void {
        this.form = this.fb.group({
            name: this.fb.control(null, [Validators.required, Validators.minLength(this.NAME_MIN_LENGTH)]),
            pageUrl: this.fb.control(null, [Validators.required]),
        });
    }

    public onConfirm(): void {
        if (this.form.invalid) {
            return;
        }

        this.form.disable({ emitEvent: false });

        this.pagesFacade.patchPage(this.pageId, {
            name: this.name.value,
            url: this.pageUrl.value,
        });

        this.siteFacade.saveSiteRequest(this.exit, this.publish, this.pageId, this.navigateTo);
    }

    public onCancel(): void {
        this.bsModalRef.hide();
    }

    @HostListener('document:keydown.enter')
    public enterKeyEvent(): void {
        if (this.form.valid) {
            this.onConfirm();
        }
    }

    public get name(): NewPageForm['name'] {
        return this.form.get('name') as NewPageForm['name'];
    }

    public get pageUrl(): NewPageForm['pageUrl'] {
        return this.form.get('pageUrl') as NewPageForm['pageUrl'];
    }

    public get pageNameAlreadyUsedError(): boolean {
        return this.name.hasError(ESavePageErrorCodes.PAGE_NAME_ALREADY_USED);
    }

    public get pageUrlAlreadyExistsError(): boolean {
        return this.pageUrl.hasError(ESavePageErrorCodes.PAGE_URL_ALREADY_USED);
    }

    public get pageUrlInvalidError(): boolean {
        return this.pageUrl.hasError(ESavePageErrorCodes.PAGE_URL_INVALID);
    }

    public get pageUrlReservedError(): boolean {
        return this.pageUrl.hasError(ESavePageErrorCodes.PAGE_URL_RESERVED);
    }

    public get url$(): Observable<string> {
        return this.domainsFacade.siteDomainUrlWithProtocol$.pipe(map((domainUrl: string) => domainUrl + '/'));
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
